import type { AsyncSequence } from "#public"

// Precomputed CRC32 Table (standard polynomial 0xEDB88320)
const CRC32_TABLE = (() => {
  const table = new Uint32Array(256)
  for (let i = 0; i < 256; i++) {
    let c = i
    for (let j = 0; j < 8; j++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1
    }
    table[i] = c >>> 0
  }
  return table
})()

/**
 * Computes CRC32 checksum of byte stream.
 *
 * Uses standard polynomial 0xEDB88320. Compatible with Python's zlib.crc32.
 *
 * @param chunks - Async sequence of Uint8Array chunks
 * @returns Promise resolving to unsigned 32-bit CRC32 value
 *
 * @example
 * ```ts
 * import { Uint8Crc32 } from "@pdfking/tfunc"
 *
 * await Uint8Crc32([new Uint8Array([1, 2, 3])])
 * ```
 */
export const Uint8Crc32 = async (chunks: AsyncSequence<Uint8Array>): Promise<number> => {
  let crc = ~0 >>> 0 // initialize to all 1s

  for await (const chunk of chunks) {
    for (let i = 0; i < chunk.length; i++) {
      crc = (crc >>> 8) ^ CRC32_TABLE[(crc ^ chunk[i]) & 0xff]
    }
  }

  return ~crc >>> 0 // invert bits and force unsigned
}
